// src/common/pages/dniUpload/guiaEnVivo/bandaDeLectura.ts
/**
 * La banda corta y fija que la guía lee antes de sacar la foto sola (decisión del usuario,
 * «Foco», 2026-10-14): si se puede leer, la foto está en foco.
 *
 *  - Frente: el encabezado «REPÚBLICA ARGENTINA», arriba a la izquierda. Lo tienen los tres
 *    modelos del catálogo (2009, 2012 y 2026).
 *  - Dorso: el comienzo del MRZ, «IDARG». Lo tienen el 2012 y el 2026; el dorso 2009 no
 *    lleva MRZ (va en su frente), y ahí la guía se queda con la nitidez (GuiaEnVivo,
 *    `lecturasFallidasSinBanda`).
 *
 * Las bandas van en fracciones de la tarjeta, con margen para la tolerancia del encuadre
 * (UMBRALES_DE_LA_GUIA); calibradas con las fotos del corpus.
 */
import type { Rectangulo } from "../reader/types";

export type CaraDeLaGuia = "frente" | "dorso";

export const BANDAS_DE_LA_GUIA: Record<CaraDeLaGuia, Rectangulo> = {
  frente: { x: 0.04, y: 0, w: 0.62, h: 0.16 },
  dorso: { x: 0, y: 0.66, w: 0.5, h: 0.2 },
};

/**
 * Lo que el OCR puede devolver en cada banda. El encabezado del frente va sin lista: medido
 * sobre el corpus, con la lista de mayúsculas el OCR no lo leía en ningún frente y sin ella
 * lo lee en la mayoría (el encabezado lleva minúsculas, guiones y el escudo al lado).
 */
export const LISTA_DE_LA_BANDA: Record<CaraDeLaGuia, string> = {
  frente: "",
  dorso: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<",
};

/** La banda en píxeles del cuadro, a partir del marco (la tarjeta, cuando la guía está en verde). */
export function bandaEnElCuadro(marco: Rectangulo, cara: CaraDeLaGuia): Rectangulo {
  const b = BANDAS_DE_LA_GUIA[cara];
  return { x: marco.x + b.x * marco.w, y: marco.y + b.y * marco.h, w: b.w * marco.w, h: b.h * marco.h };
}

function distancia(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[a.length][b.length];
}

/**
 * Si el texto que dio el OCR de la banda es la banda, con la tolerancia del OCR: en el
 * frente, «REPUBLICA» o «ARGENTINA» con hasta dos letras cambiadas; en el dorso, «IDARG»
 * con hasta una (sin espacios: el OCR los mete en el MRZ).
 */
export function leeLaBanda(texto: string, cara: CaraDeLaGuia): boolean {
  const t = texto.toUpperCase();
  if (cara === "frente") {
    const palabras = t.split(/[^A-Z]+/).filter((p) => p.length >= 7);
    return palabras.some((p) => ["REPUBLICA", "ARGENTINA"].some((r) => distancia(p, r) <= 2));
  }
  const corrido = t.replace(/\s+/g, "");
  for (let i = 0; i + 5 <= corrido.length; i++) {
    if (distancia(corrido.slice(i, i + 5), "IDARG") <= 1) return true;
  }
  return false;
}
